package br.com.htms.inspeccaocampo.data.logs

import br.com.htms.inspeccaocampo.auth.AuthApi
import br.com.htms.inspeccaocampo.data.JsonMap
import br.com.htms.inspeccaocampo.export.ZipStoreWriter
import br.com.htms.inspeccaocampo.platform.formatDateTimeBr

/** Módulos com log próprio. [id] é o mesmo usado pelo Worker em GET /logs/modulos. */
enum class ModuloLog(val id: String, val rotulo: String, val descricao: String) {
    ACESSO("ACESSO", "Acesso", "Logins, falhas de login, acessos negados e manual"),
    INSPECOES("INSPECOES", "Inspeções", "Formulários, checklists, envios, fotos e e-mails"),
    ORDEM_SERVICO("ORDEM_SERVICO", "Ordem de Serviço", "Abertura do módulo, emissão e falhas de Ordens de Serviço"),
    FICHA_EPI("FICHA_EPI", "Ficha de EPI", "Abertura do módulo, emissão e falhas de Fichas de EPI"),
    CERTIFICADOS("CERTIFICADOS", "Certificados", "Abertura do módulo, emissão e falhas de certificados"),
    APR("APR", "APR", "Abertura do módulo e geração de APR (PDF/Excel)"),
    REM("REM", "REM", "Relatório de Medições (REM)"),
    ACOMP_AMB("ACOMP_AMB", "Acomp. Ambiental", "Acompanhamento ambiental"),
    REL_SEG("REL_SEG", "Rel. Segurança", "Relatório de segurança"),
    FUNCIONARIOS("FUNCIONARIOS", "Funcionários", "Cadastro e documentos de funcionários"),
    COLABORADORES("COLABORADORES", "Colaboradores", "Cadastro, importação e regras de colaboradores"),
    ATIVOS("ATIVOS", "Ativos", "Controle de ativos, movimentações e anexos"),
    EFETIVO("EFETIVO", "Efetivo", "Lançamentos de efetivo"),
    OCORRENCIAS("OCORRENCIAS", "Ocorrências", "Registro e tratamento de ocorrências"),
    DASHBOARDS("DASHBOARDS", "Dashboards", "Acessos ao módulo de dashboards"),
    ESTOQUE("ESTOQUE", "Estoque", "Saldos, movimentações e patrimônio"),
    ADMIN("ADMIN", "Administração", "Usuários, obras, configuração e lixeira"),
    OUTROS("OUTROS", "Outros", "Eventos que não pertencem a nenhum módulo acima")
}

data class LogModulo(
    val createdAt: Long,
    val usuario: String,
    val nome: String,
    val acao: String,
    val alvo: String,
    val detalhe: String,
    val ip: String
)

/** Nome legível da ação; códigos sem tradução aparecem como estão. */
fun rotuloAcaoLog(acao: String, detalhe: String = ""): String {
    val salvoNao = detalhe.contains("salvo=nao")
    return when (acao.uppercase()) {
        "OS_OPEN", "FICHA_EPI_OPEN", "CERTIFICADO_OPEN", "APR_OPEN", "DASHBOARD_OPEN", "REM_OPEN",
        "ATIVO_OPEN", "COLABORADOR_OPEN", "EFETIVO_OPEN", "OCORRENCIA_OPEN", "ESTOQUE_OPEN" -> "Abriu o módulo"
        "ESTOQUE_MOV" -> "Movimentou estoque"
        "ESTOQUE_MOV_DEL" -> "Excluiu movimentação"
        "ESTOQUE_PAT" -> "Cadastrou patrimônio"
        "ESTOQUE_PAT_DEL" -> "Excluiu patrimônio"
        "OS_EMITIDO", "FICHA_EPI_EMITIDO", "CERTIFICADO_EMITIDO" ->
            if (salvoNao) "Emitido (não salvo no cadastro)" else "Emitido"
        "APR_EMITIDO" -> "APR gerada"
        "OS_FAIL", "FICHA_EPI_FAIL", "CERTIFICADO_FAIL", "APR_FAIL" -> "Falha ao gerar"
        "FUNCIONARIO_DOC_EMITIDO" -> "Documento emitido e salvo no cadastro"
        "FUNCIONARIO_DOC_ANEXO" -> "Documento anexado"
        "FUNCIONARIO_DOC_REMOVIDO" -> "Documento removido"
        "FUNCIONARIO_CREATE" -> "Funcionário cadastrado"
        "FUNCIONARIO_UPDATE" -> "Funcionário atualizado"
        "COLABORADOR_CREATE" -> "Colaborador cadastrado"
        "COLABORADOR_UPDATE" -> "Colaborador atualizado"
        "COLABORADOR_DELETE" -> "Colaborador removido"
        "COLABORADOR_IMPORT" -> "Importação de planilha"
        "COLABORADOR_REGRAS" -> "Regras alteradas"
        "LOGIN_OK" -> "Login OK"
        "LOGIN_FAIL" -> "Login falhou"
        "LOGIN_BLOCKED" -> "Login bloqueado"
        "LOGOUT" -> "Saiu"
        "AUTH_DENIED" -> "Acesso negado"
        "MANUAL_OPEN" -> "Abriu o manual"
        "FORM_OPEN" -> "Abriu formulário"
        "CHECKLIST_OPEN" -> "Abriu checklist"
        "FORM_SAVE" -> "Salvou formulário"
        "FORM_SAVE_FAIL" -> "Tentou salvar (pendências)"
        "FORM_SEND" -> when {
            detalhe.contains("result=fail") -> "Envio falhou"
            detalhe.contains("result=start") -> "Iniciou envio"
            else -> "Enviou formulário"
        }
        "INSPECTION_UPLOAD" -> "Gravado no servidor"
        "INSPECTION_UPLOAD_FAIL" -> "Upload falhou"
        "INSPECTION_TRASH" -> "Inspeção → lixeira"
        "INSPECTION_RESTORE" -> "Inspeção recuperada"
        "INSPECTION_PURGE" -> "Inspeção apagada"
        "EMAIL_OK" -> "E-mail enviado"
        "EMAIL_FAIL" -> "E-mail falhou"
        "PHOTO_UPLOAD" -> "Foto enviada"
        "PHOTO_UPLOAD_FAIL", "PHOTO_CAPTURE_FAIL" -> "Falha na foto"
        "USER_CREATE" -> "Usuário criado"
        "USER_UPDATE" -> "Usuário atualizado"
        "USER_DELETE" -> "Usuário removido"
        "OBRA_CREATE" -> "Obra criada"
        "OBRA_UPDATE" -> "Obra atualizada"
        "APP_CONFIG_UPDATE" -> "Configuração atualizada"
        else -> acao
    }
}

/** Linha curta do log. Esconde chave do arquivo, navegador e outros campos técnicos. */
fun resumoDetalheLog(alvo: String, detalhe: String): String {
    val pares = paresLog(detalhe)
    if (pares.isEmpty()) {
        return listOf(alvo, detalhe).filter { it.isNotBlank() }.joinToString(" · ")
    }
    val bits = mutableListOf<String>()
    if (alvo.isNotBlank() && alvo != pares["formId"]) bits.add(alvo)
    pares["form"]?.let { bits.add(it) }
    pares["bytes"]?.toLongOrNull()?.let { bits.add(tamanhoLog(it)) }
    pares["type"]?.let { bits.add(tipoArquivoLog(it)) }
    pares["reason"]?.let { bits.add(motivoFotoLog(it)) }
    pares["obra"]?.let { bits.add(it) }
    pares["local"]?.let { bits.add(it) }
    pares["tst"]?.let { bits.add(it) }
    pares["nome"]?.let { bits.add(it) }
    pares["error"]?.let { bits.add(it) }
    pares["result"]?.let { bits.add(resultadoLog(it)) }
    pares["status"]?.let { bits.add("HTTP $it") }
    pares["to"]?.let { bits.add(it) }
    pares["email"]?.let { bits.add(it) }
    return bits.filter { it.isNotBlank() }.distinct().joinToString(" · ")
}

fun tamanhoLog(n: Long): String = when {
    n < 1024 -> "$n B"
    n < 1024 * 1024 -> "${(n + 512) / 1024} KB"
    else -> {
        val mb = n / (1024.0 * 1024.0)
        val txt = (mb * 10).toLong() / 10.0
        "$txt MB"
    }
}

private fun paresLog(detalhe: String): Map<String, String> {
    if (!detalhe.contains("=")) return emptyMap()
    val map = linkedMapOf<String, String>()
    detalhe.split(";").forEach { part ->
        val i = part.indexOf("=")
        if (i <= 0) return@forEach
        val k = part.substring(0, i).trim()
        val v = part.substring(i + 1).trim()
        if (k.isNotEmpty() && v.isNotEmpty() && k !in OCULTOS_LOG) map[k] = v
    }
    return map
}

private val OCULTOS_LOG = setOf("ua", "key", "platform", "appVer")

private fun tipoArquivoLog(type: String): String = when (type.substringAfter("/", type).lowercase()) {
    "jpeg", "jpg" -> "JPEG"
    "png" -> "PNG"
    "webp" -> "WEBP"
    "gif" -> "GIF"
    else -> type.substringAfter("/", type).uppercase()
}

private fun motivoFotoLog(reason: String): String = when (reason) {
    "bad-token" -> "token inválido"
    "empty" -> "arquivo vazio"
    "too-large" -> "arquivo grande demais"
    "invalid-image" -> "imagem inválida"
    else -> reason
}

private fun resultadoLog(result: String): String = when (result) {
    "ok" -> "confirmado"
    "fail" -> "falhou"
    "start" -> "iniciado"
    "requeue" -> "reenfileirado"
    else -> result
}

object LogsModulosApi {
    suspend fun listar(
        modulo: ModuloLog,
        fromMs: Long? = null,
        toMs: Long? = null,
        user: String? = null,
        limit: Int = 2000
    ): Result<List<LogModulo>> {
        val q = buildList {
            add("modulo=${modulo.id}")
            add("limit=$limit")
            fromMs?.let { add("from=$it") }
            toMs?.let { add("to=$it") }
            user?.takeIf { it.isNotBlank() }?.let { add("user=${enc(it)}") }
        }.joinToString("&")
        return AuthApi.apiJson("GET", "/logs/modulos?$q").mapCatching { text ->
            JsonMap.parse(text).array("logs").orEmpty().mapNotNull { x ->
                val o = x as? JsonMap ?: return@mapNotNull null
                LogModulo(
                    createdAt = o.long("createdAt") ?: 0L,
                    usuario = o.str("usuario"),
                    nome = o.str("nome"),
                    acao = o.str("acao"),
                    alvo = o.str("alvo"),
                    detalhe = o.str("detalhe"),
                    ip = o.str("ip")
                )
            }
        }
    }

    private fun enc(s: String): String = buildString {
        for (b in s.encodeToByteArray()) {
            val c = b.toInt().toChar()
            if (c.isLetterOrDigit() && b >= 0 || c == '-' || c == '_' || c == '.' || c == '~') append(c)
            else append('%').append(((b.toInt() and 0xFF).toString(16).uppercase()).padStart(2, '0'))
        }
    }
}

/** Planilha .xlsx de logs: aba "Resumo" + uma aba por módulo informado. */
object PlanilhaLogs {
    const val MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"

    private val CABECALHO = listOf("Data/hora", "Usuário", "Nome", "Ação", "Código", "Alvo", "Detalhe", "IP")

    fun gerar(porModulo: Map<ModuloLog, List<LogModulo>>, periodo: String): ByteArray {
        val resumo = folha(
            linhas = listOf(listOf("Módulo", "Registros", "Último registro", "Descrição")) +
                porModulo.map { (m, logs) ->
                    listOf(
                        m.rotulo,
                        logs.size.toString(),
                        logs.maxOfOrNull { it.createdAt }?.let(::formatDateTimeBr) ?: "—",
                        m.descricao
                    )
                } +
                listOf(emptyList(), listOf("Período: $periodo")),
            larguras = listOf(24.0, 12.0, 20.0, 60.0)
        )
        val abas = listOf("Resumo" to resumo) + porModulo.map { (m, logs) ->
            nomeAba(m.rotulo) to folha(
                linhas = listOf(CABECALHO) + logs.map {
                    listOf(
                        formatDateTimeBr(it.createdAt), it.usuario, it.nome,
                        rotuloAcaoLog(it.acao, it.detalhe), it.acao, it.alvo, it.detalhe, it.ip
                    )
                },
                larguras = listOf(18.0, 18.0, 28.0, 32.0, 26.0, 30.0, 80.0, 16.0)
            )
        }
        val workbook = buildString {
            append(XML_DECL)
            append("""<workbook xmlns="$NS_MAIN" xmlns:r="$NS_REL"><bookViews><workbookView activeTab="0"/></bookViews><sheets>""")
            abas.forEachIndexed { i, (nome, _) ->
                append("""<sheet name="${esc(nome)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>""")
            }
            append("</sheets></workbook>")
        }
        val workbookRels = buildString {
            append(XML_DECL)
            append("""<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""")
            abas.indices.forEach { i ->
                append("""<Relationship Id="rId${i + 1}" Type="$NS_REL/worksheet" Target="worksheets/sheet${i + 1}.xml"/>""")
            }
            append("""<Relationship Id="rId${abas.size + 1}" Type="$NS_REL/styles" Target="styles.xml"/>""")
            append("</Relationships>")
        }
        val contentTypes = buildString {
            append(XML_DECL)
            append("""<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">""")
            append("""<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>""")
            append("""<Default Extension="xml" ContentType="application/xml"/>""")
            append("""<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>""")
            abas.indices.forEach { i ->
                append("""<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>""")
            }
            append("""<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>""")
            append("</Types>")
        }
        val rootRels = XML_DECL +
            """<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">""" +
            """<Relationship Id="rId1" Type="$NS_REL/officeDocument" Target="xl/workbook.xml"/></Relationships>"""
        val entradas = linkedMapOf(
            "[Content_Types].xml" to contentTypes,
            "_rels/.rels" to rootRels,
            "xl/workbook.xml" to workbook,
            "xl/_rels/workbook.xml.rels" to workbookRels,
            "xl/styles.xml" to STYLES
        )
        abas.forEachIndexed { i, (_, xml) -> entradas["xl/worksheets/sheet${i + 1}.xml"] = xml }
        return ZipStoreWriter.write(entradas.mapValues { it.value.encodeToByteArray() })
    }

    /** Excel limita o nome da aba a 31 caracteres e proíbe : \ / ? * [ ] */
    private fun nomeAba(s: String): String =
        s.filterNot { it in ":\\/?*[]" }.take(31)

    private fun folha(linhas: List<List<String>>, larguras: List<Double>): String = buildString {
        append(XML_DECL)
        append("""<worksheet xmlns="$NS_MAIN" xmlns:r="$NS_REL">""")
        append("""<sheetViews><sheetView workbookViewId="0">""")
        append("""<pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>""")
        append("</sheetView></sheetViews>")
        append("""<sheetFormatPr defaultRowHeight="15"/><cols>""")
        larguras.forEachIndexed { i, w ->
            append("""<col min="${i + 1}" max="${i + 1}" width="$w" customWidth="1"/>""")
        }
        append("</cols><sheetData>")
        linhas.forEachIndexed { r, cells ->
            append("""<row r="${r + 1}">""")
            cells.forEachIndexed { c, v ->
                val estilo = if (r == 0) 1 else 0
                append("""<c r="${coluna(c)}${r + 1}" s="$estilo" t="inlineStr"><is><t xml:space="preserve">${esc(v)}</t></is></c>""")
            }
            append("</row>")
        }
        append("</sheetData>")
        if (linhas.size > 1) {
            append("""<autoFilter ref="A1:${coluna(larguras.size - 1)}${linhas.size}"/>""")
        }
        append("</worksheet>")
    }

    private fun coluna(i: Int): String {
        var n = i + 1
        val sb = StringBuilder()
        while (n > 0) {
            val r = (n - 1) % 26
            sb.insert(0, ('A' + r))
            n = (n - 1) / 26
        }
        return sb.toString()
    }

    private fun esc(s: String): String = buildString(s.length) {
        for (ch in s) when {
            ch == '&' -> append("&amp;")
            ch == '<' -> append("&lt;")
            ch == '>' -> append("&gt;")
            ch == '"' -> append("&quot;")
            ch < ' ' && ch != '\n' && ch != '\t' -> append(' ')
            else -> append(ch)
        }
    }

    private const val XML_DECL = """<?xml version="1.0" encoding="UTF-8" standalone="yes"?>"""
    private const val NS_MAIN = "http://schemas.openxmlformats.org/spreadsheetml/2006/main"
    private const val NS_REL = "http://schemas.openxmlformats.org/officeDocument/2006/relationships"

    /** Estilos: 0 padrão, 1 cabeçalho (negrito branco em azul). */
    private val STYLES = XML_DECL +
        """<styleSheet xmlns="$NS_MAIN">""" +
        """<fonts count="2"><font><sz val="11"/><name val="Calibri"/></font>""" +
        """<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font></fonts>""" +
        """<fills count="3"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>""" +
        """<fill><patternFill patternType="solid"><fgColor rgb="FF1F4E78"/><bgColor indexed="64"/></patternFill></fill></fills>""" +
        """<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>""" +
        """<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>""" +
        """<cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>""" +
        """<xf numFmtId="49" fontId="1" fillId="2" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf></cellXfs>""" +
        """<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>""" +
        """</styleSheet>"""
}
